import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { object as yupObject, string as yupString } from "yup";

import { CheckboxGroup, TelephoneMask, TextInput } from "@icg360/design-system";

import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { phoneNumberYupString } from "consts";
import { usePolicyDetail, useUser } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { TingContext, type TingContextState } from "pages/offer/ting/ting-flow";

import styles from "./ting-offer.module.scss";

const schema = yupObject()
  .shape({
    firstName: yupString().required("Please enter your first name."),
    lastName: yupString().required("Please enter your last name."),
    emailAddress: yupString()
      .required("Please enter an email address.")
      .email("Please enter a valid email address."),
    phone: phoneNumberYupString(),
  })
  .required();

type ContactFormFields = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phone: string;
};

export const ContactInformation = () => {
  const user = useUser();
  const { data } = usePolicyDetail();
  const { state, setFieldsAndNavigate, formNavigate } =
    useMultiStepFormState<TingContextState>(TingContext);
  const [mobile, setMobile] = useState<string[]>(state?.mobile ?? []);
  const skipPropertyStep = state?.eligibleTingPolicies?.length === 1;
  const stepLabels = skipPropertyStep
    ? ["Confirm details", "Review and finish"]
    : ["Select properties", "Confirm details", "Review and finish"];

  // phone isn't in the default values here
  // but is passed in below because of how the PhoneNumber mask works
  const defaultValues = {
    firstName: state?.firstName ?? user?.firstName ?? "",
    lastName: state?.lastName ?? user?.lastName ?? "",
    emailAddress:
      state?.emailAddress ??
      data?.userDetails.primaryInsured?.emailAddress ??
      "",
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ContactFormFields>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  const onSubmit = (data) => {
    setFieldsAndNavigate("../contact-information-confirmation", {
      mobile,
      ...data,
    });
  };

  const onMobileChange = (value) => {
    if (!value.includes("isTextOk")) {
      setMobile([]);
    } else {
      setMobile(value);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <MultiStepFormLayout
        showLogo
        labels={stepLabels}
        step={skipPropertyStep ? 1 : 2}
        totalSteps={skipPropertyStep ? 2 : 3}
        heading="Confirm your contact details"
        subheading="Ting will get in touch to share updates about your shipment."
        buttonProps={{
          primary: { type: "submit", disabled: !isValid },
          cancel: { path: "/my/property-services/ting-offer" },
          promptOnCancel: true,
          formNavigate,
        }}
      >
        <div className={styles.contactNameFields}>
          <div className={styles.contactNameField}>
            <TextInput
              isRequired
              label="First name"
              {...register("firstName")}
              errorMessage={errors.firstName?.message}
              isError={!!errors.firstName}
              data-testid="shingle-firstname"
            />
          </div>
          <div className={styles.contactNameField}>
            <TextInput
              isRequired
              label="Last name"
              {...register("lastName")}
              errorMessage={errors.lastName?.message}
              isError={!!errors.lastName}
              data-testid="shingle-lastname"
            />
          </div>
        </div>
        <TextInput
          isRequired
          label="Email"
          {...register("emailAddress")}
          errorMessage={errors.emailAddress?.message}
          isError={!!errors.emailAddress}
          data-testid="shingle-email"
        />
        <TelephoneMask
          isRequired
          label="Phone number"
          {...register("phone")}
          errorMessage={errors.phone?.message}
          isError={!!errors.phone}
          value={state?.phone?.replace(/\D+/g, "") ?? ""}
          data-testid="shingle-phone"
        />
        <CheckboxGroup onChange={onMobileChange} value={mobile}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.checkboxWrapper}>
            <CheckboxGroup.Checkbox value="isTextOk" multiline />I consent to
            receive automated important delivery and installation updates via
            text/SMS to the phone number I have provided from Whisker Labs. I
            own or am the primary user of this number, and will notify Whisker
            Labs if the number changes. I can reply with &quot;STOP&quot; at any
            time to be automatically unsubscribed from text/SMS messages.
            Message and data rates may apply.
          </label>
        </CheckboxGroup>
      </MultiStepFormLayout>
    </form>
  );
};
